import { Button, HStack, Stack, StackProps, TableHeaderProps } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import React, { useRef } from "react";
import { PrimaryActionButton } from "@/components/PrimaryActionButton";
import {
  FilterExpressionType,
  LogicalExpressionOperator,
  LogicalFilterExpression,
  RelationalExpressionLeftOperandType,
  RelationalExpressionOperator,
  RelationalExpressionRightOperandType,
} from "../../types";
import { LogicalExpressionForm } from "./LogicalExpressionForm";
import { ArticleFilterResults } from "../ArticleFilterResults";
import NavigableTree, { NavigableTreeItem } from "../../../messageBuilder/components/NavigableTree";
import { getAriaLabelForExpressionGroup } from "./utils/getAriaLabelForExpressionGroup";
import { SavedUnsavedChangesPopupBar } from "@/components";

interface FormData {
  expression: LogicalFilterExpression | null;
}

interface Props {
  expression?: LogicalFilterExpression | null;
  onSave: (expression: LogicalFilterExpression | null) => Promise<void>;
  formContainerProps?: StackProps;
  previewContainerProps?: {
    thead: TableHeaderProps;
  };
  previewTitle?: React.ReactNode;
}

export const FiltersForm = ({
  expression = null,
  onSave,
  previewTitle,
  previewContainerProps,
  formContainerProps,
}: Props) => {
  const { t } = useTranslation();
  const formMethods = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      expression,
    },
  });
  const {
    handleSubmit,
    control,
    formState: { isDirty, isSubmitting },
    setValue,
    reset,
  } = formMethods;

  const watchedExpression = useWatch({
    control,
    name: "expression",
  });
  const formFocusRef = useRef<HTMLFormElement>(null);

  const onDeletedExpression = async () => {
    setValue("expression", null, {
      shouldDirty: true,
    });
  };

  const onSaveExpression = async ({ expression: finalExpression }: FormData) => {
    try {
      await onSave(finalExpression);
      reset({
        expression: finalExpression,
      });
    } catch (err) {}
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // By default, this submit will also trigger wrapping forms to submit.
    e.stopPropagation();

    return handleSubmit(onSaveExpression)(e);
  };

  const addInitialExpression = () => {
    setValue(
      "expression",
      {
        type: FilterExpressionType.Logical,
        op: LogicalExpressionOperator.And,
        children: [
          {
            type: FilterExpressionType.Relational,
            op: RelationalExpressionOperator.Equals,
            left: {
              type: RelationalExpressionLeftOperandType.Article,
              value: "",
            },
            right: {
              type: RelationalExpressionRightOperandType.String,
              value: "",
            },
          },
        ],
      },
      {
        shouldDirty: true,
      },
    );
  };

  if (!watchedExpression) {
    return (
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <Stack gap={8}>
            <Stack>
              <Button onClick={addInitialExpression}>
                <span>
                  {t("features.feedConnections.components.filtersForm.addNewFiltersButtonText")}
                </span>
              </Button>
              <HStack justifyContent="flex-end">
                <PrimaryActionButton type="submit" loading={isSubmitting} disabled={!isDirty}>
                  <span>{t("common.buttons.save")}</span>
                </PrimaryActionButton>
              </HStack>
            </Stack>
            <ArticleFilterResults
              filters={watchedExpression}
              title={previewTitle}
              tableContainer={{
                theadProps: previewContainerProps?.thead,
              }}
            />
          </Stack>
        </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form ref={formFocusRef} onSubmit={onSubmit} aria-label="Article filters settings">
        <Stack gap={12}>
          <Stack>
            <NavigableTree accessibleLabel="Filter expression">
              <NavigableTreeItem
                isRootItem
                id="expression."
                ariaLabel={getAriaLabelForExpressionGroup(watchedExpression.op)}
              >
                <LogicalExpressionForm
                  onDeleted={onDeletedExpression}
                  prefix="expression."
                  containerProps={formContainerProps}
                />
              </NavigableTreeItem>
            </NavigableTree>
          </Stack>
          <ArticleFilterResults
            filters={watchedExpression}
            title={previewTitle}
            tableContainer={{
              theadProps: previewContainerProps?.thead,
            }}
          />
        </Stack>
        <SavedUnsavedChangesPopupBar useDirtyFormCheck restoreFocusRef={formFocusRef} />
      </form>
    </FormProvider>
  );
};
